import { produce } from 'immer';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resources } from '../../apps/benefit/resources/resources';
import { customEvents } from '../constants';
import { ToastTypes } from '../enums';
import { config } from '../global/config';
import useMount from '../hooks/useMount';
import Icons from './Icons';

export interface ToastDetail {
  message?: string;
  type?: ToastTypes;
  showCloseButton?: boolean;
  undoAction?: () => void;
  toastVisibilityTimeMs?: number;
  isSticky?: boolean;
  customContent?: ReactNode;
  customContentClass?: string;
}

interface ToastState extends ToastDetail {
  isVisible: boolean;
}

export const Toast = () => {
  const [state, setState] = useState<ToastState>({ isVisible: false });
  const { t: translate } = useTranslation();

  const closeToast = () => {
    setState({ isVisible: false });
  };

  useMount(() => {
    document.addEventListener(customEvents.showToast, (e: any) => {
      setState(
        produce(d => {
          d.isVisible = true;
          d.message = e.detail.message;
          d.type = e.detail.type;
          d.showCloseButton = e.detail.showCloseButton;
          d.undoAction = e.detail.undoAction;
          d.isSticky = e.detail.isSticky;
          d.toastVisibilityTimeMs = e.detail.toastVisibilityTimeMs;
          d.customContent = e.detail.customContent;
          d.customContentClass = e.detail.customContentClass;
        }),
      );

      if (!e.detail.isSticky) {
        setTimeout(() => {
          closeToast();
        }, e.detail.toastVisibilityTimeMs ?? config.toastVisibilityTimeMs);
      }
    });
  });

  if (!state.isVisible || !state.type) return null;

  const toastDetails = getToastDetails(state.type);

  return (
    <div
      className={`toast ${toastDetails.className} ${
        state.customContentClass || ''
      }`}
    >
      <div className="toast-icon">{toastDetails.icon}</div>
      <div className="toast-content">
        {state.customContent ? state.customContent : <p>{state.message}</p>}

        {state.undoAction && (
          <div className="toast-action">
            <a
              onClick={() => {
                state.undoAction!();
                closeToast();
              }}
            >
              <span className="btn-text">{translate(resources.cancel)}</span>
              <span className="btn-icon">
                <Icons.ToastActionArrow />
              </span>
            </a>
          </div>
        )}
      </div>
      <div className="toast-close">
        {state.showCloseButton && (
          <button onClick={closeToast}>
            <Icons.NotificationsClose />
          </button>
        )}
      </div>
    </div>
  );
};

const getToastDetails = (
  type: ToastTypes,
): { className: string; icon: ReactNode } => {
  switch (type) {
    case ToastTypes.Cart:
      return { className: 't-cart', icon: <Icons.ToastShoppingCart /> };
    case ToastTypes.Success:
      return { className: 't-success', icon: <Icons.ToastSuccess /> };
    case ToastTypes.Info:
      return { className: 't-info', icon: <Icons.ToastInfo /> };
    case ToastTypes.Error:
      return { className: 't-error', icon: <Icons.ToastError /> };
  }
};
